import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, User, Building2 } from "lucide-react";
import { useMessageCenterTarget, useEditProfileTarget } from "./APPnavTargets"; // same folder
import NotificationBell from "./NotificationBell";
import AccountMenu from "./AccountMenu";
import SiftWordmark from "./SiftWordmark";
import ConciergeBar from "./ConciergeBar";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { useBranding } from "../contexts/BrandingContext";
import { useStreakPing } from "../hooks/useStreakPing";

/**
 * Personal ↔ Company dashboard switch. Only shown when the user actually has a
 * company; the active side is highlighted. Makes swapping obvious (esp. mobile),
 * replacing the old "go to /home and use a dropdown" path.
 */
function DashboardSwitch({ mode, full = false }) {
  const seg = (active) =>
    `inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-pill text-xs font-semibold transition-colors ${
      active ? "bg-brand text-white" : "text-secondary hover:text-ink"
    } ${full ? "flex-1" : ""}`;
  return (
    <div className={`inline-flex items-center rounded-pill border border-line bg-surface p-0.5 ${full ? "w-full" : ""}`}>
      <Link to="/dashboard/contractor" className={seg(mode === "personal")} aria-current={mode === "personal"}>
        <User className="h-3.5 w-3.5" /> Personal
      </Link>
      <Link to="/company/dashboard" className={seg(mode === "company")} aria-current={mode === "company"}>
        <Building2 className="h-3.5 w-3.5" /> Company
      </Link>
    </div>
  );
}

// Global navigator targets. Company set when the user has an active org,
// otherwise the contractor set; a few shared verbs are always included.
const CONTRACTOR_ACTIONS = [
  { label: "Dashboard", href: "/dashboard/contractor", kw: "home overview" },
  { label: "My Assignments", href: "/dashboard/contractor/assignments", kw: "jobs gigs work assignments" },
  { label: "Milestones", href: "/dashboard/contractor/milestones", kw: "milestones progress" },
  { label: "Timecard", href: "/dashboard/contractor/timecard", kw: "log time hours timecard" },
  { label: "Clock in", href: "/clock", kw: "clock in out punch shift start" },
  { label: "Contract Center", href: "/dashboard/contractor/contract-center", kw: "contracts agreements sign" },
  { label: "Documents to sign", href: "/dashboard/contractor/documents", kw: "documents to sign signature" },
  { label: "Notices", href: "/dashboard/notices", kw: "notices announcements broadcast" },
  { label: "Calendar", href: "/dashboard/contractor/calendar", kw: "calendar schedule availability" },
  { label: "Travel Center", href: "/dashboard/contractor/travel", kw: "travel trips" },
  { label: "Postings", href: "/dashboard/contractor/postings", kw: "postings find work openings" },
  { label: "Inbound Requests", href: "/dashboard/contractor/inbox", kw: "inbound requests messages" },
  { label: "My Requests", href: "/dashboard/my-requests", kw: "my requests services requested" },
  { label: "Public Profile Editor", href: "/dashboard/contractor/storefront", kw: "profile storefront edit public page" },
  { label: "Credentials", href: "/dashboard/contractor/credentials", kw: "credentials licenses certifications" },
  { label: "Accounting Center", href: "/dashboard/contractor/accounting", kw: "accounting income expenses" },
  { label: "Tax Center", href: "/dashboard/contractor/taxes", kw: "tax taxes 1099" },
  { label: "Get paid", href: "/dashboard/contractor/stripe", kw: "stripe payout bank deposit get paid connect" },
  { label: "My profile", href: "/dashboard/contractor/profile", kw: "profile resume skills availability" },
];
const COMPANY_ACTIONS = [
  { label: "Dashboard", href: "/company/dashboard", kw: "home overview" },
  { label: "Calendar", href: "/company/calendar", kw: "calendar schedule" },
  { label: "Contracts", href: "/company/contracts", kw: "contracts agreements" },
  { label: "Documents to sign", href: "/company/documents", kw: "documents to sign signature" },
  { label: "Notices", href: "/company/notices", kw: "notices announcements broadcast" },
  { label: "Workforce Center", href: "/company/workforce", kw: "workforce headcount" },
  { label: "Purchase Orders", href: "/company/purchase-orders", kw: "po purchase orders" },
  { label: "Credential Center", href: "/company/credentials", kw: "credentials licenses compliance" },
  { label: "Timecards", href: "/company/timecards", kw: "timecards hours approve" },
  { label: "Time Off", href: "/company/time-off", kw: "time off pto leave" },
  { label: "Milestones", href: "/company/milestones", kw: "milestones payments" },
  { label: "Travel Center", href: "/company/travel", kw: "travel trips" },
  { label: "Inbound Requests", href: "/company/inbound-requests", kw: "inbound requests leads storefront" },
  { label: "Postings", href: "/company/postings", kw: "postings hire job openings" },
  { label: "Storefront Editor", href: "/company/storefront", kw: "storefront public page edit" },
  { label: "Team", href: "/company/team", kw: "team members roles" },
  { label: "Reporting Center", href: "/company/finance", kw: "finance reporting" },
  { label: "Income Statement", href: "/company/finance/income", kw: "income statement revenue" },
  { label: "Billing & Stripe", href: "/company/billing", kw: "billing stripe subscription payout" },
  { label: "Post a need", href: "/company/postings", kw: "post hire job opening recruit need" },
];

export default function AppNavbar() {
  const [open, setOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasOrg, setHasOrg] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const branding = useBranding();
  useStreakPing(); // records the daily login + toasts the streak once per day

  // Dynamic targets (company vs contractor) based on current route / me()
  const messageCenterHref = useMessageCenterTarget();
  const editProfileHref = useEditProfileTarget();

  useEffect(() => {
    fetchWithAuth("/api/accounts/me/")
      .then(data => {
        if (data?.is_platform_admin) setIsAdmin(true);
        // Company present via the new shape OR an older memberships array.
        const orgPresent = !!(
          data?.organization?.id ||
          data?.memberships?.length ||
          data?.organization_memberships?.length
        );
        setHasOrg(orgPresent);
      })
      .catch(() => {});
  }, []);

  // Which dashboard are we currently in? Drives the Personal/Company switch.
  const dashMode = location.pathname.startsWith("/company") ? "company" : "personal";
  // The switch only makes sense on standard (non cream-label) accounts that
  // actually have a company to swap to.
  const showDashSwitch = hasOrg && !branding.branded;

  const navActions = hasOrg ? COMPANY_ACTIONS : CONTRACTOR_ACTIONS;

  const doLogout = async () => {
    try {
      await fetch("/api/accounts/logout/", { method: "POST", credentials: "include" });
    } catch (_) {}
    try {
      localStorage.removeItem("authToken");
      localStorage.removeItem("resumeToastShown");
    } catch (_) {}
    navigate("/auth/login");
  };

  // Light Sift workspace top bar. Branded (cream-label) keeps the partner accent.
  const onCream = branding.branded;
  const linkBase = "px-3 py-1.5 rounded-pill transition-colors text-sm md:text-base";
  const active = onCream
    ? "text-white font-semibold"
    : "bg-brand-tint text-brand font-semibold";
  const inactive = onCream ? "text-white/80 hover:bg-white/10" : "text-secondary hover:bg-sunken hover:text-ink";
  const brandAccent = onCream ? branding.primary_color : null;

  return (
    <header
      className="sticky top-0 z-50 pt-[env(safe-area-inset-top)]"
      style={{
        backgroundColor: onCream ? branding.accent_color : "rgba(250,248,242,0.92)",
        backdropFilter: onCream ? undefined : "saturate(180%) blur(12px)",
        WebkitBackdropFilter: onCream ? undefined : "saturate(180%) blur(12px)",
        borderBottom: onCream ? "1px solid rgba(255,255,255,0.18)" : "1px solid var(--border)",
      }}
    >
      <div className="max-w-[1480px] mx-auto px-4 sm:px-6 lg:px-10 h-16 md:h-20 flex items-center justify-between">
        {/* Brand */}
        <Link to="/home" className="select-none flex items-center">
          {onCream && branding.brand_logo_url ? (
            <img src={branding.brand_logo_url} alt={branding.brand_name} className="h-8 max-w-[160px] object-contain" />
          ) : onCream && branding.brand_name ? (
            <span className={
              branding.logo_font_style === "uppercase_bold"
                ? "uppercase tracking-[0.25em] font-extrabold text-xl text-white"
                : branding.logo_font_style === "italic_serif"
                  ? "italic font-serif font-semibold text-xl text-white"
                  : "font-semibold text-xl text-white"
            }>
              {branding.brand_name}
            </span>
          ) : (
            <SiftWordmark size={30} />
          )}
        </Link>

        {/* Global navigator — present on every logged-in page (desktop) */}
        <div className="hidden md:flex flex-1 min-w-0 justify-center px-6 max-w-2xl mx-auto">
          <ConciergeBar actions={navActions} placeholder="Search Sift or jump to anything…" />
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-2">
          <NavLink
            to="/home"
            data-tour="my-sift"
            className={({ isActive }) =>
              `${linkBase} ${isActive ? active : inactive}`
            }
          >
            My Sift
          </NavLink>

          {/* ARCHIVED 2026-06-11: Messaging Center retired.
          <NavLink
            to={messageCenterHref}
            data-tour="nav-messages"
            className={({ isActive }) =>
              `${linkBase} ${isActive ? active : inactive}`
            }
          >
            Message Center
          </NavLink>
          */}

          {/* Platform Admin — hidden on cream label */}
          {isAdmin && !branding.branded && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `${linkBase} ${isActive ? active : inactive}`
              }
            >
              Admin
            </NavLink>
          )}

          {/* Personal ↔ Company dashboard switch */}
          {showDashSwitch && <DashboardSwitch mode={dashMode} />}

          {/* Notification Bell */}
          <span data-tour="nav-notifications" className="inline-flex">
            <NotificationBell />
          </span>

          {/* Account avatar + menu (edit profile, My Sift, log out) */}
          <span data-tour="nav-profile" className="inline-flex">
            <AccountMenu size={40} />
          </span>
        </nav>

        {/* Mobile: Notification Bell + Menu toggle */}
        <div className="md:hidden flex items-center gap-2">
          <NotificationBell />
          <button
            onClick={() => setOpen((s) => !s)}
            className="p-2 rounded-lg border border-line text-ink hover:bg-sunken"
            aria-expanded={open ? "true" : "false"}
            aria-controls="appnav-mobile"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Personal ↔ Company switch — always visible on mobile so swapping is
          obvious without opening the menu. */}
      {showDashSwitch && (
        <div className="md:hidden px-4 pb-2">
          <DashboardSwitch mode={dashMode} full />
        </div>
      )}

      {/* Global navigator — mobile row (always visible under the bar) */}
      <div className="md:hidden px-4 pb-3 -mt-1 flex">
        <ConciergeBar actions={navActions} placeholder="Search Sift…" />
      </div>

      {/* Mobile panel */}
      {open && (
        <div id="appnav-mobile" className="md:hidden border-t border-line bg-surface">
          <nav className="px-4 py-3 grid gap-2">
            <NavLink
              to="/home"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? active : inactive}`
              }
            >
              My Sift
            </NavLink>

            {/* ARCHIVED 2026-06-11: Messaging Center retired.
            <NavLink
              to={messageCenterHref}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? active : inactive}`
              }
            >
              Message Center
            </NavLink>
            */}

            <NavLink
              to={editProfileHref}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? active : inactive}`
              }
            >
              Edit Profile
            </NavLink>

            {isAdmin && !branding.branded && (
              <NavLink
                to="/admin"
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `${linkBase} ${isActive ? active : inactive}`
                }
              >
                Admin
              </NavLink>
            )}

            <button
              onClick={() => {
                setOpen(false);
                doLogout();
              }}
              className="px-3 py-2 rounded-pill text-sm text-left font-semibold transition-colors"
              style={brandAccent
                ? { backgroundColor: brandAccent, color: "#fff" }
                : { backgroundColor: "var(--brand)", color: "#fff" }}
            >
              Logout
            </button>

            {/* "Delete account" used to sit here, one thin border below the
                Logout button, and it was a red full-width tap target directly
                under the thing everyone reaches for. On a phone that is a
                near-miss waiting to happen, and it was happening.

                It lives on Edit Profile now, for both audiences: the contractor
                profile page always had it, and the company page did not until
                this change. That mattered -- with the menu entry gone and no
                company-side equivalent, an owner would have had no in-app route
                at all, which the privacy policy promises and which Apple's
                guideline 5.1.1(v) requires of an app that creates accounts.

                Nothing is lost by moving it. /account/delete makes you type
                DELETE MY ACCOUNT, so the menu link was never one tap from
                deletion; it was one tap from a fright. */}
          </nav>
        </div>
      )}
    </header>
  );
}